import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const C = { primary:'#2E7D32', primaryDark:'#1B5E20', border:'#E0E0E0', light:'#F4F6F9', green100:'#E8F5E9', textDark:'#212121', textMid:'#616161', textLight:'#9E9E9E' };

const ACTIONS = ['CONNEXION', 'CREATION', 'MODIFICATION', 'SUPPRESSION', 'VALIDATION', 'SIGNATURE', 'CORRECTION', 'IMPORT', 'EXPORT', 'ACTIVATION', 'DESACTIVATION'];
const MODULES = ['AUTH', 'UTILISATEURS', 'PRODUCTION', 'FACTURATION', 'PARAMETRES'];

const actionColor = (action) => {
  const colors = {
    CONNEXION: '#2980b9', CREATION: '#1a5c2a', MODIFICATION: '#e67e22',
    SUPPRESSION: '#c0392b', VALIDATION: '#27ae60', SIGNATURE: '#8e44ad',
    CORRECTION: '#d35400', IMPORT: '#16a085', EXPORT: '#2980b9',
    ACTIVATION: '#27ae60', DESACTIVATION: '#c0392b',
  };
  return colors[action] || '#888';
};

const statutColor = (statut) => statut === 'OK' ? '#27ae60' : statut === 'ERREUR' ? '#c0392b' : '#e67e22';

export default function AuditLogPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [page, setPage] = useState(1);
  const PAR_PAGE = 20;

  const today = new Date().toISOString().split('T')[0];
  const [filtres, setFiltres] = useState({
    action: '', module: '', date_debut: today, date_fin: today, utilisateur_id: '', limit: 200
  });
  const [detail, setDetail] = useState(null);

  const charger = async () => {
    setChargement(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filtres).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await api.get(`/users/audit?${params}`);
      setLogs(r.data);
      setPage(1);
    } catch (e) {
      console.error(e);
    } finally { setChargement(false); }
  };

  useEffect(() => { charger(); }, []);

  const totalPages = Math.ceil(logs.length / PAR_PAGE);
  const logsPage = logs.slice((page - 1) * PAR_PAGE, page * PAR_PAGE);

  const S = {
    page: { minHeight: '100vh', background: C.light },
    topbar: { height: 52, background: '#fff', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px', position: 'sticky', top: 0, zIndex: 100 },
    breadcrumb: { fontSize: 13, color: C.textLight, display: 'flex', alignItems: 'center', gap: 6 },
    breadcrumbActive: { color: C.textDark, fontWeight: 600 },
    container: { maxWidth: 1500, margin: '20px auto', padding: '0 20px', paddingBottom: 40 },
    carteFiltre: { background: 'white', borderRadius: 10, padding: '12px 16px', marginBottom: 16, border: `0.5px solid ${C.border}`, boxShadow: '0 1px 3px rgba(0,0,0,.05)' },
    grille: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, alignItems: 'flex-end' },
    label: { display: 'block', fontSize: 12, color: C.textMid, marginBottom: 4, fontWeight: 600 },
    input: { width: '100%', padding: '7px 9px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, boxSizing: 'border-box', outline: 'none' },
    carte: { background: 'white', borderRadius: 12, border: `0.5px solid ${C.border}`, boxShadow: '0 1px 3px rgba(0,0,0,.06)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
    th: { background: '#F8F9FA', color: C.textMid, padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' },
    td: { padding: '8px 10px', borderBottom: '1px solid #F0F2F4', verticalAlign: 'middle', color: C.textDark },
    btnSearch: { background: C.primary, color: 'white', border: 'none', padding: '8px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700, width: '100%' },
    pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '12px 16px', borderTop: `1px solid ${C.border}`, background: '#FAFAFA' },
    btnPage: (a) => ({ padding: '5px 11px', borderRadius: 6, border: `1px solid ${a ? C.primary : C.border}`, background: a ? C.primary : 'white', color: a ? 'white' : C.textDark, cursor: 'pointer', fontSize: 12, fontWeight: a ? 700 : 400 }),
  };

  // Résumé stats
  const stats = {
    total: logs.length,
    erreurs: logs.filter(l => l.statut === 'ERREUR').length,
    connexions: logs.filter(l => l.action === 'CONNEXION').length,
    modifications: logs.filter(l => ['MODIFICATION', 'CREATION', 'SUPPRESSION'].includes(l.action)).length,
  };

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <div style={S.breadcrumb}>
          <span>Administration</span>
          <span style={{ color: C.border }}>›</span>
          <span style={S.breadcrumbActive}>Journal d'audit</span>
        </div>
        <button style={{ background: 'transparent', border: 'none', color: C.textMid, fontSize: 13, cursor: 'pointer' }} onClick={() => navigate('/users')}>← Utilisateurs</button>
      </div>

      <div style={S.container}>
        {/* Stats rapides */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
          {[
            { label: 'Total actions', val: stats.total, color: C.textDark, bg: C.light, icon: '📊' },
            { label: 'Connexions', val: stats.connexions, color: '#1565C0', bg: '#E3F2FD', icon: '🔐' },
            { label: 'Modifications', val: stats.modifications, color: '#E65100', bg: '#FFF3E0', icon: '✏️' },
            { label: 'Erreurs', val: stats.erreurs, color: '#C62828', bg: '#FFEBEE', icon: '⚠️' },
          ].map(s => (
            <div key={s.label} style={{ background: 'white', borderRadius: 10, padding: '16px 18px', border: `0.5px solid ${C.border}`, boxShadow: '0 1px 3px rgba(0,0,0,.06)', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 42, height: 42, background: s.bg, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{s.icon}</div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 'bold', color: s.color }}>{s.val}</div>
                <div style={{ fontSize: 11, color: C.textLight }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filtres */}
        <div style={S.carteFiltre}>
          <div style={{ fontWeight: 700, color: C.textDark, marginBottom: 10, fontSize: 13 }}>🔍 Filtres</div>
          <div style={S.grille}>
            <div>
              <label style={S.label}>Action</label>
              <select style={S.input} value={filtres.action} onChange={e => setFiltres({ ...filtres, action: e.target.value })}>
                <option value="">Toutes</option>
                {ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Module</label>
              <select style={S.input} value={filtres.module} onChange={e => setFiltres({ ...filtres, module: e.target.value })}>
                <option value="">Tous</option>
                {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Date début</label>
              <input style={S.input} type="date" value={filtres.date_debut} onChange={e => setFiltres({ ...filtres, date_debut: e.target.value })} />
            </div>
            <div>
              <label style={S.label}>Date fin</label>
              <input style={S.input} type="date" value={filtres.date_fin} onChange={e => setFiltres({ ...filtres, date_fin: e.target.value })} />
            </div>
            <div>
              <label style={S.label}>Nb résultats max</label>
              <select style={S.input} value={filtres.limit} onChange={e => setFiltres({ ...filtres, limit: e.target.value })}>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </div>
            <div>
              <button style={S.btnSearch} onClick={charger}>🔍 Rechercher</button>
            </div>
          </div>
          <div style={{ marginTop: 10, color: C.textMid, fontSize: 12 }}>
            {chargement ? '⏳ Chargement...' : `📊 ${logs.length} action(s) trouvée(s) — Page ${page}/${totalPages || 1}`}
          </div>
        </div>

        {/* Tableau */}
        <div style={S.carte}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Date/Heure</th>
                <th style={S.th}>Utilisateur</th>
                <th style={S.th}>Rôle</th>
                <th style={S.th}>Action</th>
                <th style={S.th}>Module</th>
                <th style={S.th}>Entité</th>
                <th style={S.th}>Statut</th>
                <th style={S.th}>IP</th>
                <th style={S.th}>Détail</th>
              </tr>
            </thead>
            <tbody>
              {logsPage.map(log => (
                <tr key={log.id} style={{ background: log.statut === 'ERREUR' ? '#fff5f5' : 'white' }}>
                  <td style={S.td}>
                    <strong style={{ fontSize: 12 }}>{new Date(log.cree_le).toLocaleString('fr-FR')}</strong>
                  </td>
                  <td style={S.td}>
                    <strong>{log.utilisateur_nom || '—'}</strong>
                  </td>
                  <td style={S.td}>
                    <span style={{ fontSize: 11, color: '#666', fontStyle: log.role ? 'normal' : 'italic' }}>{log.role || '—'}</span>
                  </td>
                  <td style={S.td}>
                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 'bold', background: actionColor(log.action) + '22', color: actionColor(log.action) }}>
                      {log.action}
                    </span>
                  </td>
                  <td style={S.td}><span style={{ fontSize: 11, color: '#555', background: '#f0f0f0', padding: '2px 8px', borderRadius: 6 }}>{log.module || '—'}</span></td>
                  <td style={S.td}>
                    <span style={{ fontSize: 12 }}>{log.entite || '—'}</span>
                    {log.entite_id && <span style={{ fontSize: 11, color: '#888' }}> #{log.entite_id}</span>}
                  </td>
                  <td style={S.td}>
                    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 'bold', background: statutColor(log.statut) + '22', color: statutColor(log.statut) }}>
                      {log.statut || 'OK'}
                    </span>
                    {log.message && <div style={{ fontSize: 11, color: '#c0392b', marginTop: 2 }}>{log.message.substring(0, 40)}</div>}
                  </td>
                  <td style={S.td}><span style={{ fontSize: 11, color: '#888', fontFamily: 'monospace' }}>{log.ip_address || '—'}</span></td>
                  <td style={S.td}>
                    {(log.details_avant || log.details_apres) && (
                      <button
                        onClick={() => setDetail(log)}
                        style={{ background: C.primary, color: 'white', border: 'none', padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 11 }}
                      >
                        👁️ Voir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!chargement && logsPage.length === 0 && (
                <tr><td colSpan="9" style={{ ...S.td, textAlign: 'center', padding: 30, color: '#888' }}>Aucune action trouvée pour ces critères.</td></tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={S.pagination}>
              <button style={S.btnPage(false)} onClick={() => setPage(1)} disabled={page === 1}>«</button>
              <button style={S.btnPage(false)} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return <button key={p} style={S.btnPage(p === page)} onClick={() => setPage(p)}>{p}</button>;
              })}
              <button style={S.btnPage(false)} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
              <button style={S.btnPage(false)} onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
              <span style={{ color: '#666', fontSize: 12 }}>Page {page}/{totalPages} — {logs.length} actions</span>
            </div>
          )}
        </div>
      </div>

      {/* Modal détail */}
      {detail && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={(e) => e.target === e.currentTarget && setDetail(null)}>
          <div style={{ background: 'white', borderRadius: 12, padding: 30, width: 600, maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: C.textDark, marginBottom: 14 }}>
              🔍 Détail — {detail.action} par {detail.utilisateur_nom}
            </div>
            <div style={{ fontSize: 12, color: C.textMid, marginBottom: 18 }}>{new Date(detail.cree_le).toLocaleString('fr-FR')}</div>
            {detail.details_avant && (
              <div style={{ marginBottom: 15 }}>
                <div style={{ fontWeight: 700, color: '#C62828', marginBottom: 8, fontSize: 13 }}>📸 Avant :</div>
                <pre style={{ background: '#FFEBEE', padding: 12, borderRadius: 6, fontSize: 12, overflow: 'auto', border: '1px solid #EF9A9A' }}>
                  {JSON.stringify(detail.details_avant, null, 2)}
                </pre>
              </div>
            )}
            {detail.details_apres && (
              <div>
                <div style={{ fontWeight: 700, color: C.primaryDark, marginBottom: 8, fontSize: 13 }}>✅ Après :</div>
                <pre style={{ background: C.green100, padding: 12, borderRadius: 6, fontSize: 12, overflow: 'auto', border: '1px solid #A5D6A7' }}>
                  {JSON.stringify(detail.details_apres, null, 2)}
                </pre>
              </div>
            )}
            <button onClick={() => setDetail(null)} style={{ marginTop: 18, background: C.primary, color: 'white', border: 'none', padding: '9px 22px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
